/**
 * Detects errors from dynamically injected scripts (e.g. Cal.com embed,
 * analytics snippets, browser extensions) where the browser cannot identify
 * the source file. These errors surface with every stack frame showing
 * "@ undefined" because the script was loaded without CORS headers or was
 * eval'd at runtime.
 *
 * Unlike `isCrossOriginScriptError` (which only matches the browser's
 * sanitized "Script error." message), this catches errors that DO include a
 * message and minified function names — but have no usable source location.
 *
 * Matches when:
 * 1. The stack trace has at least one `@ undefined` frame
 * 2. Every frame references "@ undefined" or "[native code]" (no source file)
 * 3. The error message is short/opaque (≤ 64 chars) — a heuristic to avoid
 *    suppressing application errors that happen to have corrupted stacks
 */
export function isSourcelessScriptErrorNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message || !stack) {
    return false;
  }

  if (message.length > 64) {
    return false;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (
    frames.length === 0 ||
    !frames.some((frame) => frame.includes("@ undefined"))
  ) {
    return false;
  }

  return frames.every(
    (frame) => frame.includes("@ undefined") || frame.includes("[native code]"),
  );
}
